import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Button, Col, DatePicker, Divider, Form, Input, InputNumber,
  Modal, Row, Select, Space, Switch, Tag, Tooltip, Upload, message,
} from "antd";
import { FileTextOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from "../config/api";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import ExpenseDocumentPreviewModal from "../gestion-depenses/components/ExpenseDocumentPreviewModal";
import { useCommercialTreasuryAccountSuggestion } from "../hooks/useCommercialTreasurySuggestion";
import { regularizeProductionVariableCost } from "./api";
import {
  buildProductionExpensePayload, formatProductionExpenseAmount as money,
  getProductionExpenseDefaults,
} from "./productionExpenseRegularization";
import "./production-expense-regularization.css";

const required = (message) => [{ required: true, message }];
const namedOptions = (rows) => rows.map((row) => ({ value: Number(row.id), label: row.name }));
const errorText = (error) => typeof error === "string" ? error : error?.response?.data?.error || error?.error || error?.message;

// Mounted only for the selected cost, shared by desktop and mobile history.
export default function ProductionExpenseRegularizationModal({ cost, details, onClose, onSuccess }) {
  const { hasPermission } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const production = details.production;
  const companyId = Number(production.company_id || selectedCompany);
  const warehouseId = Number(production.warehouse_id || selectedWarehouse);
  const canCreateSupplier = hasPermission("Gestion Commerciale.Entites.Fournisseurs.create");
  const canCreateCategory = hasPermission("Gestion Commerciale.Depenses.Categories.create");
  const canPay = hasPermission("Gestion Commerciale.Depenses.SaisieDepenses.create_payment");
  const canCreateExpense = hasPermission("Gestion Commerciale.Depenses.SaisieDepenses.create");
  const contextMismatch = !companyId || !warehouseId ||
    (selectedCompany && Number(selectedCompany) !== companyId) ||
    (selectedWarehouse && Number(selectedWarehouse) !== warehouseId);
  const [form] = Form.useForm();
  const [quickForm] = Form.useForm();
  const [defaults] = useState(() => getProductionExpenseDefaults(production, cost));
  const [suppliers, setSuppliers] = useState(details.variable_cost_regularization_options?.suppliers || []);
  const [categories, setCategories] = useState(details.variable_cost_regularization_options?.expense_categories || []);
  const [quickType, setQuickType] = useState(null);
  const [quickSaving, setQuickSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const submittingRef = useRef(false);
  const quickSubmittingRef = useRef(false);
  const [submitError, setSubmitError] = useState("");
  const [quickError, setQuickError] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const uploadedDocument = useRef(null);
  const [paymentModes, setPaymentModes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentLoadError, setPaymentLoadError] = useState("");
  const [paymentReload, setPaymentReload] = useState(0);
  const supplierId = Form.useWatch("supplier_id", form);
  const wantsPayment = Form.useWatch("add_payment", form);
  const paymentAmount = Form.useWatch("payment_amount", form);
  const addPayment = Boolean(canPay && supplierId && wantsPayment);
  const { onModeChange: suggestTreasuryAccount } =
    useCommercialTreasuryAccountSuggestion(form, {
      warehouseId,
      enabled: addPayment && !contextMismatch,
      modeField: "payment_mode_id",
      accountField: "treasury_account_id",
      accounts,
    });
  const amount = Number(cost.total_cost);
  const amountPaid = addPayment ? Number(paymentAmount || 0) : 0;
  const remaining = Math.round((amount - amountPaid) * 100) / 100;
  const locked = saving || completed || quickSaving || Boolean(contextMismatch) || !canCreateExpense;

  useEffect(() => {
    if (!file) { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!addPayment || contextMismatch) return undefined;
    let active = true;
    setPaymentLoading(true);
    setPaymentLoadError("");
    Promise.all([
      axios.get(API_CONFIG.apiUrl("/payment-modes"), { params: { company_id: companyId, limit: 200 } }),
      axios.get(API_CONFIG.apiUrl("/payment-modes"), { params: { limit: 200 } }),
      axios.get(API_CONFIG.apiUrl("/treasury/accounts"), {
        params: { company_id: companyId, warehouse_id: warehouseId, module_scope: "commercial", include_inactive: false },
      }),
    ]).then(([companyModes, allModes, treasury]) => {
      if (!active) return;
      const modes = [...(companyModes.data?.paymentModes || []), ...(allModes.data?.paymentModes || [])]
        .filter((mode) => !mode.company_id || Number(mode.company_id) === companyId);
      setPaymentModes(Array.from(new Map(modes.map((mode) => [Number(mode.id), mode])).values()));
      setAccounts(treasury.data?.success ? treasury.data.data || [] : []);
    }).catch((error) => {
      if (!active) return;
      setPaymentLoadError(errorText(error) || "Impossible de charger les moyens de paiement.");
      setPaymentModes([]);
      setAccounts([]);
    }).finally(() => { if (active) setPaymentLoading(false); });
    return () => { active = false; };
  }, [addPayment, companyId, warehouseId, contextMismatch, paymentReload]);

  const openQuickCreate = (type) => {
    if (locked || (type === "supplier" ? !canCreateSupplier : !canCreateCategory)) return;
    quickForm.resetFields();
    setQuickError("");
    setQuickType(type);
  };

  const saveQuickCreate = async (values) => {
    if (quickSubmittingRef.current || contextMismatch ||
        (quickType === "supplier" ? !canCreateSupplier : !canCreateCategory)) return;
    quickSubmittingRef.current = true;
    setQuickSaving(true);
    setQuickError("");
    try {
      const name = values.name.trim();
      const isSupplier = quickType === "supplier";
      const payload = isSupplier ? {
        name, email: values.email?.trim() || null, phone: values.phone?.trim() || null,
        address: values.address?.trim(), user_type: "suppliers", status: "enabled",
        company_id: companyId, warehouse_id: warehouseId, details_warehouse_id: warehouseId,
      } : { name, description: values.description?.trim() || "", company_id: companyId, module_scope: "commercial" };
      const response = await axios.post(API_CONFIG.apiUrl(isSupplier ? "/users" : "/expenses/categories"), payload);
      const entity = { id: Number(response.data.id), name };
      if (!entity.id) throw new Error("Création effectuée, mais référence indisponible. Actualisez les détails de la production.");
      if (isSupplier) {
        setSuppliers((current) => [...current.filter((item) => Number(item.id) !== entity.id), entity]);
        form.setFieldValue("supplier_id", entity.id);
      } else {
        setCategories((current) => [...current.filter((item) => Number(item.id) !== entity.id), entity]);
        form.setFieldValue("expense_category_id", entity.id);
      }
      setQuickType(null);
      message.success(isSupplier ? "Fournisseur créé et sélectionné." : "Catégorie créée et sélectionnée.");
    } catch (error) {
      setQuickError(errorText(error) || "Création impossible.");
    } finally {
      quickSubmittingRef.current = false;
      setQuickSaving(false);
    }
  };

  const chooseDocument = (nextFile) => {
    if (!/\.(pdf|jpe?g|png)$/i.test(nextFile.name) ||
        !["application/pdf", "image/jpeg", "image/png"].includes(nextFile.type)) {
      message.error("Choisissez un PDF, JPEG ou PNG.");
      return Upload.LIST_IGNORE;
    }
    if (nextFile.size > 10 * 1024 * 1024) {
      message.error("Le justificatif ne doit pas dépasser 10 Mo.");
      return Upload.LIST_IGNORE;
    }
    uploadedDocument.current = null;
    setFile(nextFile);
    return false;
  };

  const submit = async (values) => {
    if (locked || submittingRef.current) return;
    if (addPayment && (paymentLoading || paymentLoadError || !accounts.length || !paymentModes.length)) return;
    submittingRef.current = true;
    setSaving(true);
    setSubmitError("");
    let result;
    try {
      // Upload only on validation, not when selecting a file or cancelling.
      // Retain the uploaded result on retry; never delete it after an uncertain
      // network response, since the expense may already have been committed.
      if (file && !uploadedDocument.current) {
        const data = new FormData();
        data.append("document", file);
        data.append("module_scope", "commercial");
        const upload = await axios.post(API_CONFIG.apiUrl("/expenses/upload-document"), data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (!upload.data?.success || !upload.data.url) throw new Error("Le justificatif n'a pas pu être enregistré.");
        uploadedDocument.current = upload.data;
      }
      const payload = buildProductionExpensePayload(values, {
        companyId, warehouseId, document: uploadedDocument.current, canPay,
      });
      result = await regularizeProductionVariableCost(production.id, cost.id, payload);
      setCompleted(true);
    } catch (error) {
      setSubmitError(errorText(error) || "Enregistrement non confirmé. Vos données sont conservées ; vous pouvez réessayer sans créer de doublon.");
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
    if (result) {
      message.success(result.message || (result.payment ? "Dépense et paiement enregistrés." : "Dépense créée."));
      onClose();
      try { await onSuccess(result); }
      catch { message.warning("Enregistrement réussi. Actualisez l'historique pour voir la mise à jour."); }
    }
  };

  const selectWithCreate = (name, label, rows, canCreate, type) => (
    <Form.Item label={label} required>
      <div className="production-expense-select">
        {canCreate && <Tooltip title={`Créer ${type === "supplier" ? "un fournisseur" : "une catégorie"}`}>
          <Button icon={<PlusOutlined />} aria-label={`Créer ${type === "supplier" ? "un fournisseur" : "une catégorie"}`}
            onClick={() => openQuickCreate(type)} />
        </Tooltip>}
        <Form.Item name={name} noStyle rules={required(`Veuillez sélectionner ${type === "supplier" ? "un fournisseur" : "une catégorie"}.`)}>
          <Select aria-label={label} showSearch allowClear optionFilterProp="label" options={namedOptions(rows)}
            placeholder={`Sélectionner ${type === "supplier" ? "un fournisseur" : "une catégorie"}`}
            onChange={name === "supplier_id" ? (value) => { if (!value) form.setFieldValue("add_payment", false); } : undefined} />
        </Form.Item>
      </div>
    </Form.Item>
  );

  return <>
    <Modal open title="Régulariser le coût variable en dépense" width={880}
      className="production-expense-modal" style={{ top: 24 }}
      maskClosable={false} keyboard={!saving && !quickSaving} closable={!saving && !quickSaving}
      onCancel={() => { if (!saving && !quickSaving) onClose(); }}
      footer={<div className="production-expense-footer">
        <span>Le coût de revient de la production reste inchangé.</span>
        <Space wrap>
          <Button disabled={saving || quickSaving} onClick={onClose}>Annuler</Button>
          <Button type="primary" loading={saving} disabled={locked || (addPayment && (paymentLoading || Boolean(paymentLoadError) || !accounts.length || !paymentModes.length))}
            onClick={() => form.submit()}>{addPayment ? "Créer la dépense et payer" : "Créer la dépense"}</Button>
        </Space>
      </div>}>
      <div className="production-expense-source">
        <div><Tag color="blue">Coût de production</Tag><h3>{cost.label}</h3>
          <p>{[production.production_unit_name, production.production_code || production.lot_code].filter(Boolean).join(" · ")}</p>
          <small>Production du {defaults.expense_date.format("DD/MM/YYYY")}</small>
        </div>
        <div className="production-expense-amount"><strong>{money(amount)}</strong><small>Montant constaté · non modifiable</small></div>
      </div>
      {contextMismatch && <Alert showIcon type="error" message="Le contexte entreprise ou établissement a changé. Fermez ce formulaire et rouvrez la production dans le bon contexte." />}
      {submitError && <Alert showIcon type="error" message={submitError} style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" initialValues={defaults} onFinish={submit} disabled={locked}>
        <Divider orientation="left">Détails de la dépense</Divider>
        <Row gutter={20}>
          <Col xs={24} sm={12}>{selectWithCreate("supplier_id", "Fournisseur", suppliers, canCreateSupplier, "supplier")}</Col>
          <Col xs={24} sm={12}>{selectWithCreate("expense_category_id", "Catégorie de dépense", categories, canCreateCategory, "category")}</Col>
          <Col xs={24} sm={12}><Form.Item name="expense_date" label="Date de la dépense" rules={required("Veuillez choisir une date.")}
            extra="Préremplie avec la date de production, librement modifiable.">
            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item></Col>
          <Col xs={24} sm={12}><Form.Item name="due_date" label="Échéance du paiement" rules={required("Veuillez choisir une échéance.")}
            extra="Date prévue pour régler le solde.">
            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
          </Form.Item></Col>
          <Col span={24}><Form.Item name="bill" label="Référence de facture ou de reçu (facultatif)"
            extra="Sans saisie, une référence de production est attribuée automatiquement.">
            <Input maxLength={191} placeholder="Ex. FAC-2026-0081" />
          </Form.Item></Col>
          <Col span={24}><Form.Item name="notes" label="Description">
            <Input.TextArea rows={3} maxLength={10000} showCount />
          </Form.Item></Col>
          <Col span={24}><Form.Item label="Pièce justificative (facultative)" extra="Facture ou reçu conseillé · PDF, JPEG ou PNG · 10 Mo maximum.">
            <Upload accept=".pdf,.jpg,.jpeg,.png" beforeUpload={chooseDocument} maxCount={1}
              fileList={file ? [{ uid: "document", name: file.name, status: "done" }] : []}
              onPreview={() => setPreviewOpen(true)}
              onRemove={() => { setFile(null); uploadedDocument.current = null; }}>
              {!file && <Button icon={<UploadOutlined />}>Joindre un justificatif</Button>}
            </Upload>
            {file && <Button type="link" icon={<FileTextOutlined />} onClick={() => setPreviewOpen(true)}>Aperçu du justificatif</Button>}
          </Form.Item></Col>
        </Row>
        <section className="production-expense-payment" aria-label="Paiement facultatif">
          <div className="production-expense-payment-heading">
            <div><h3>Enregistrer un paiement maintenant</h3><p>Facultatif · règlement partiel ou total</p></div>
            <Form.Item name="add_payment" valuePropName="checked" style={{ margin: 0 }}>
              <Switch disabled={locked || !canPay || !supplierId} aria-label="Enregistrer un paiement maintenant" />
            </Form.Item>
          </div>
          {!canPay ? <p>Votre profil ne permet pas d’enregistrer un paiement.</p>
            : !supplierId ? <p>Sélectionnez d’abord un fournisseur pour activer le paiement.</p> : null}
          {addPayment && <>
            {paymentLoadError && <Alert showIcon type="error" message={paymentLoadError}
              action={<Button size="small" onClick={() => setPaymentReload((value) => value + 1)}>Réessayer</Button>} />}
            {!paymentLoading && !paymentLoadError && (!accounts.length || !paymentModes.length) &&
              <Alert showIcon type="warning" message="Aucun compte ou mode de paiement disponible. Désactivez le paiement pour créer uniquement la dépense, ou faites compléter la configuration." />}
            <Row gutter={20} style={{ marginTop: 16 }}>
              <Col xs={24} sm={12}><Form.Item name="payment_date" label="Date du paiement" rules={required("Choisissez la date du paiement.")}>
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="payment_amount" label="Montant à payer" rules={[
                { required: true, type: "number", min: 0.01, max: amount, message: `Saisissez un montant entre 0,01 et ${money(amount)}.` },
              ]}>
                <InputNumber min={0.01} max={amount} precision={2} addonAfter="CFA" style={{ width: "100%" }} />
              </Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="treasury_account_id" label="Compte de trésorerie" rules={required("Sélectionnez le compte à débiter.")}>
                <Select showSearch optionFilterProp="label" options={namedOptions(accounts)} loading={paymentLoading} placeholder="Choisir le compte à débiter" />
              </Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item name="payment_mode_id" label="Mode de paiement" rules={required("Sélectionnez un mode de paiement.")}>
                <Select showSearch optionFilterProp="label" options={namedOptions(paymentModes)} loading={paymentLoading} placeholder="Choisir un mode" onChange={suggestTreasuryAccount} />
              </Form.Item></Col>
              <Col span={24}><Form.Item name="payment_notes" label="Notes du paiement (facultatives)">
                <Input.TextArea rows={2} maxLength={10000} />
              </Form.Item></Col>
            </Row>
          </>}
        </section>
        <dl className="production-expense-summary" aria-live="polite">
          <div><dt>Dépense</dt><dd>{money(amount)}</dd></div>
          <div><dt>Paiement immédiat</dt><dd>{money(amountPaid)}</dd></div>
          <div><dt>Reste à payer</dt><dd>{money(Math.max(remaining, 0))}</dd></div>
        </dl>
      </Form>
    </Modal>
    <Modal open={Boolean(quickType)} title={quickType === "supplier" ? "Créer un fournisseur" : "Créer une catégorie de dépense"}
      onCancel={() => { if (!quickSaving) setQuickType(null); }} maskClosable={false} closable={!quickSaving}
      onOk={() => quickForm.submit()} confirmLoading={quickSaving} okButtonProps={{ disabled: Boolean(contextMismatch) }}
      cancelButtonProps={{ disabled: quickSaving }} okText="Créer et sélectionner" cancelText="Annuler">
      <p>Création dans l’entreprise de la production{quickType === "supplier" ? " et rattachement à son établissement" : ""}. Votre saisie en cours est conservée.</p>
      {quickError && <Alert showIcon type="error" message={quickError} style={{ marginBottom: 16 }} />}
      <Form form={quickForm} layout="vertical" onFinish={saveQuickCreate} disabled={quickSaving} preserve={false}>
        <Form.Item name="name" label={quickType === "supplier" ? "Nom ou raison sociale" : "Nom de la catégorie"}
          rules={[{ required: true, whitespace: true, message: "Veuillez saisir un nom." }]}><Input maxLength={191} autoFocus /></Form.Item>
        {quickType === "supplier" ? <>
          <Row gutter={16}>
            <Col xs={24} sm={12}><Form.Item name="phone" label="Téléphone (facultatif)"><Input maxLength={50} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="email" label="Email (facultatif)" rules={[{ type: "email", message: "Email invalide." }]}><Input maxLength={191} /></Form.Item></Col>
          </Row>
          <Form.Item name="address" label="Adresse" rules={[{ required: true, whitespace: true, message: "Veuillez saisir l’adresse." }]}><Input.TextArea rows={2} maxLength={1000} /></Form.Item>
        </> : <Form.Item name="description" label="Description (facultative)"><Input.TextArea rows={3} maxLength={1000} /></Form.Item>}
      </Form>
    </Modal>
    <ExpenseDocumentPreviewModal open={previewOpen} url={previewUrl} fileName={file?.name}
      contentType={file?.type} onClose={() => setPreviewOpen(false)} />
  </>;
}
